import { createFormatForDescription as cffd } from '../createFormatForDescription'
import explainAmount from '../explain/explainAmount'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import CheckCreateInstructions from '../types/instructions/CheckCreateInstructions'
import TransactionType from '../types/TransactionType'
import CurrencyAmount from '../types/util/CurrencyAmount'
import { parseDate } from '../util'

/**
 * An explanation of a CheckCreate transaction.
 *
 * @property type - The transaction type.
 * @property destination - An explanation of the destination.
 * @property sendMax - An explanation of the sendMax.
 * @property destinationTag - An explanation of the destinationTag.
 * @property expiration - An explanation of the expiration.
 * @property invoiceId - An explanation of the invoiceID.
 */
export interface CheckCreateAnnotation {
  readonly type: TransactionType.CheckCreate
  readonly destination: Explanation<string>
  readonly sendMax: Explanation<CurrencyAmount>
  readonly destinationTag?: Explanation<number>
  readonly expiration?: Explanation<number>
  readonly invoiceId?: Explanation<string>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: CheckCreateInstructions,
): CheckCreateAnnotation {
  const destination = explain(
    transactionInstructions.Destination,
    `The account with address ${cffd(
      transactionInstructions.Destination,
      'destination',
    )} can cash the check.`,
  )

  const sendMax = explain(
    transactionInstructions.SendMax,
    `This check can be cashed for up to ${explainAmount(
      transactionInstructions.SendMax,
    )}`,
  )

  const destinationTag = explainOptional(
    transactionInstructions.DestinationTag,
    (tag) =>
      `This tag identifies a hosted recipient or other information for the recipient of the deleted account's leftover XRP: ${cffd(
        tag.toString(),
        'destinationTag',
      )}`,
  )

  const expiration = explainOptional(
    transactionInstructions.Expiration,
    (theExpiration) =>
      `This check will expire on (${cffd(
        parseDate(theExpiration),
        'expiration',
      )}).`,
  )

  const invoiceId = explainOptional(
    transactionInstructions.InvoiceID,
    (invoice) =>
      `The hash representing a specific reason or identifier for this check is ${cffd(
        invoice,
        'invoiceId',
      )}`,
  )

  return {
    destination,
    sendMax,
    ...(destinationTag && { destinationTag }),
    ...(expiration && { expiration }),
    ...(invoiceId && { invoiceId }),

    type: TransactionType.CheckCreate,
  }
}
